/* components/patterns/Overlays.jsx — составные оверлеи поверх Drawer/Modal.
   FilterDrawer      — фильтры: на xs → bottom-sheet (.select-sheet, max-height 85dvh),
                       на остальных ширинах → Drawer size=sm + футер «Сбросить / Готово».
   EntityPickerModal — каркас async-выбора сущности: поиск (debounce 250),
                       список результатов, multi/single, «Добавить выбранное».
                       Доменная логика подключается снаружи (props).
   ХЕНДОФФ → frontend/components/common/. */

import React from 'react';
import ReactDOM from 'react-dom';
import { Button } from '../ui/Button.jsx';
import { Drawer } from '../ui/Drawer.jsx';
import { Checkbox, SearchInput } from '../ui/Field.jsx';
import { Icon } from '../ui/Icon.jsx';
import { Modal } from '../ui/Modal.jsx';
import { hooks } from '../../hooks/index.jsx';
import { getOverlayRoot, overlay } from '../../lib/overlay.jsx';

  const { useState, useEffect, useRef, useMemo } = React;
  const { useBreakpoint } = hooks;
  const { useFocusTrap } = overlay;

  /* ── FilterDrawer ── */
  function FilterSheet({ onClose, onReset, onApply, title, children, activeCount }) {
    const sheetRef = useRef(null);
    useFocusTrap(sheetRef, { active: true, onEscape: onClose });
    // .select-sheet даёт нижний лист (скругление сверху, slide-up). max-height 85dvh —
    // по спеке 03; body скроллится, футер закреплён. Раскладка — инлайн (без новых CSS-классов).
    return ReactDOM.createPortal(
      <div className="overlay overlay--sheet" style={{ zIndex: 'var(--z-modal)' }}>
        <div className="overlay__backdrop" onClick={onClose} />
        <div className="select-sheet" ref={sheetRef} role="dialog" aria-modal="true" aria-label={title}
          style={{ maxHeight: '85dvh' }}>
          <div className="select-sheet__head">
            <span className="t-h4 grow">{title}{activeCount ? ` · ${activeCount}` : ''}</span>
            <button type="button" className="modal__close" onClick={onClose} aria-label="Закрыть"><Icon name="close" size={18} /></button>
          </div>
          <div className="stack stack-16" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', padding: 'var(--space-16)' }}>{children}</div>
          <div className="cluster cluster-8" style={{ flexShrink: 0, padding: 'var(--space-12) calc(var(--space-16)) calc(var(--space-12) + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid var(--divider)', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={onReset}>Сбросить</Button>
            <Button variant="primary" onClick={() => { onApply && onApply(); onClose && onClose(); }}>Готово</Button>
          </div>
        </div>
      </div>,
      getOverlayRoot()
    );
  }

  function FilterDrawer({ open, onClose, onReset, onApply, title = 'Фильтры', children, activeCount }) {
    const { bp } = useBreakpoint();
    if (open && bp === 'xs') {
      return <FilterSheet onClose={onClose} onReset={onReset} onApply={onApply} title={title} activeCount={activeCount}>{children}</FilterSheet>;
    }
    return (
      <Drawer open={open} onClose={onClose} title={title} size="sm"
        breadcrumbs={activeCount ? `Активно: ${activeCount}` : undefined}
        footer={<>
          <Button variant="ghost" onClick={onReset}>Сбросить</Button>
          <Button variant="primary" onClick={() => { onApply && onApply(); onClose && onClose(); }}>Готово</Button>
        </>}>
        <div className="stack stack-16">{children}</div>
      </Drawer>
    );
  }

  /* ── EntityPickerModal ── */
  function EntityPickerModal({ open, onClose, title = 'Выбор', placeholder = 'Поиск…',
                               items = null, fetcher, multi = false, onSubmit, renderItem, submitLabel, size = 'sm' }) {
    const [q, setQ] = useState('');
    const [results, setResults] = useState(items || []);
    const [loading, setLoading] = useState(false);
    const [picked, setPicked] = useState([]);
    const timer = useRef(null);

    useEffect(() => { if (!open) { setQ(''); setPicked([]); setResults(items || []); } }, [open]);

    useEffect(() => {
      if (!open || !fetcher) return;
      setLoading(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(async () => {
        try { const r = await fetcher(q); setResults(r || []); } catch (e) { setResults([]); }
        setLoading(false);
      }, 250);
      return () => timer.current && clearTimeout(timer.current);
    }, [q, open]);

    const shown = useMemo(() => {
      if (fetcher) return results;
      if (!q) return results;
      const s = q.toLowerCase();
      return results.filter((it) => (it.label || it.name || '').toLowerCase().includes(s));
    }, [q, results]);

    const toggle = (it) => {
      const id = it.id ?? it.value;
      if (multi) setPicked((p) => p.find((x) => (x.id ?? x.value) === id) ? p.filter((x) => (x.id ?? x.value) !== id) : [...p, it]);
      else { onSubmit && onSubmit([it]); onClose && onClose(); }
    };
    const isPicked = (it) => picked.some((x) => (x.id ?? x.value) === (it.id ?? it.value));

    return (
      <Modal open={open} onClose={onClose} title={title} size={size}
        footer={multi ? <>
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button variant="primary" disabled={!picked.length} onClick={() => { onSubmit && onSubmit(picked); onClose && onClose(); }}>
            {submitLabel || `Добавить${picked.length ? ' · ' + picked.length : ''}`}
          </Button>
        </> : undefined}>
        <div className="stack stack-12">
          <SearchInput value={q} onChange={setQ} placeholder={placeholder} />
          <div className="picker-list">
            {loading
              ? <div className="picker-list__state"><Icon name="spinner" size={22} /></div>
              : shown.length === 0
                ? <div className="picker-list__state t-body-sm text-mute">Ничего не найдено</div>
                : shown.map((it) => (
                  <button type="button" key={it.id ?? it.value} className={'picker-item' + (isPicked(it) ? ' is-picked' : '')} onClick={() => toggle(it)}>
                    {multi && <Checkbox checked={isPicked(it)} onChange={() => {}} />}
                    <span className="grow" style={{ minWidth: 0 }}>
                      {renderItem ? renderItem(it) : <>
                        <span className="picker-item__label truncate">{it.label || it.name}</span>
                        {it.hint && <span className="picker-item__hint t-caption truncate">{it.hint}</span>}
                      </>}
                    </span>
                    {!multi && <Icon name="chevron-right" size={16} className="text-mute" />}
                  </button>
                ))}
          </div>
        </div>
      </Modal>
    );
  }

  /* ── LanguagePicker ── единое честное решение (как AuthFooter): интерфейс пока
     только на русском; никакого «клика в никуда» и фейкового English-пункта. */
  function LanguagePicker({ open, onClose }) {
    return (
      <Modal open={open} onClose={onClose} title="Язык интерфейса" size="xs"
        footer={<Button variant="primary" onClick={onClose}>Понятно</Button>}>
        <div className="stack stack-8">
          <div className="lang-opt is-active" aria-current="true">
            <span className="grow" style={{ minWidth: 0 }}>
              <span className="lang-opt__label">Русский</span>
            </span>
            <Icon name="check" size={16} className="text-accent" />
          </div>
          <p className="t-body-sm text-dim">Другие языки появятся позже.</p>
        </div>
      </Modal>
    );
  }

export { FilterDrawer };
export { EntityPickerModal };
export { LanguagePicker };
